import { useCallback } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TFunction } from 'i18next'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView, CustomLayout, TraceSuffix } from '../../../chart-kit/shared/utils/chartModel'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { getToDateValue } from 'src/Libs/system-data-handler/utils/datetime'

export const getComparisonYears = (from: Moment, prevFrom: Moment): TraceSuffix[] => {
  const currYear = from.year()
  const prevYear = prevFrom.year()

  return [
    {
      name: prevYear.toString(),
    },
    {
      name: currYear.toString(),
    },
  ]
}

export const useSystemChartCallbacks = (
  from: Moment,
  to: Moment,
  customRange: boolean,
  showForecast: boolean,
  view: ChartView,
  maxFrom: Moment | null,
  maxTo: Moment | null,
  setView: (value: ChartView) => void,
  setFrom: (value: Moment) => void,
  setTo: (value: Moment) => void,
  setCustomRange: (value: boolean) => void,
  setIsForecastEnabled?: (value: boolean) => void,
  minFrom?: string /** earliest date which can be selected, e.g. the ownership date */
): [
  handleViewChange: (view: ChartView) => void,
  handleFromChange: (from: Moment | null) => void,
  handleToChange: (to: Moment | null) => void,
  handleCustomRangeChange: (customRange: boolean) => void,
  handlePlus: () => void,
  handleMinus: () => void,
  handleShowForecast: (value: boolean) => void
] => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()

  const handleSwitchToYear = useCallback(() => {
    const newFromValue = from.clone().startOf('year')
    const newToValue = customRange ? from.clone().add(12, 'months') : newFromValue.clone().endOf('year')

    if (!customRange) setFrom(newFromValue)
    setTo(newToValue)
  }, [customRange, from, setFrom, setTo])

  const handleSwitchingToWeek = useCallback(
    (newViewValue: ChartView) => {
      const newFromValue = from.clone().startOf(from.date() === 1 ? 'day' : 'week')
      const newToValue = customRange ? from.clone().add(1, newViewValue) : newFromValue.clone().add(1, newViewValue)

      if (!customRange) setFrom(newFromValue)
      setTo(newToValue)
    },
    [customRange, from, setFrom, setTo]
  )

  /**
   * Handles logic if moving from a larger to a smaller view (e.g. year to day).
   */
  const handleSwitchToLowerView = useCallback(
    (currentView: ChartView, targetView: ChartView) => {
      const today = moment()
      const isCurrentYear = today.year() === from.year()
      const isCurrentMonth = isCurrentYear && today.month() === from.month()
      const isCurrentWeek = isCurrentYear && isCurrentMonth && today.week() === from.week()

      const useCurrentDateAsBasis =
        isCurrentWeek || (currentView === 'month' && isCurrentMonth && isCurrentYear) || (currentView === 'year' && isCurrentYear)
      const baseDate = useCurrentDateAsBasis ? today : from
      const targetFromValue = baseDate.clone().startOf(targetView)
      const minFromValue = minFrom ? moment(minFrom).startOf(targetView) : undefined
      const newFromValue = minFromValue && targetFromValue.isBefore(minFromValue) ? minFromValue : targetFromValue
      const newToValue = newFromValue.clone().add(1, targetView)

      setFrom(newFromValue)
      setTo(newToValue)
    },
    [from, minFrom, setFrom, setTo]
  )

  const handleGoToCurrentView = useCallback(
    (newViewValue: ChartView) => {
      const today = moment()
      setFrom(today.clone().startOf(newViewValue))
      setTo(today.clone().endOf(newViewValue))
    },
    [setFrom, setTo]
  )

  /**
   * Updates the chart view (day, ..., year) which also requires updating the from and to value in which range data is displayed.
   * When moving from a larger to a smaller view (e.g. year to day) there is a difference in the logic based on if its a date in the current year or not.
   * current year: move to the current month, week, day (e.g. today is 21.08.2025. If year 2025 selected and user moved to day the 21.08.2025 is shown)
   * past year: move to the first month, week, day (e.g. today is 21.08.2025. If year 2024 selected and user moved to day the 01.01.2024 is shown)
   */
  const handleViewChange = useCallback(
    (newViewValue: ChartView) => {
      setView(newViewValue)

      const isSwitchingToYear = newViewValue === 'year'
      const isSwitchingToWeek = newViewValue === 'week'
      const isSwitchingFromYearToWeek = view === 'year' && newViewValue === 'week'
      const isSwitchingFromMonthToWeek = view === 'month' && newViewValue === 'week'
      const isSwitchingFromYearToMonth = view === 'year' && newViewValue === 'month'
      const isSwitchingToDay = newViewValue === 'day'
      const isSwitchingToSameView = newViewValue === view

      if (isSwitchingToSameView) {
        handleGoToCurrentView(newViewValue)
        return
      }

      if (isSwitchingFromMonthToWeek && customRange) return

      if (isSwitchingToYear) {
        handleSwitchToYear()
        return
      }

      if (isSwitchingFromYearToMonth || isSwitchingFromYearToWeek || isSwitchingFromMonthToWeek || isSwitchingToDay) {
        handleSwitchToLowerView(view, newViewValue)
        return
      }

      if (isSwitchingToWeek) {
        handleSwitchingToWeek(newViewValue)
        return
      }

      const newFromValue = from.clone().startOf(newViewValue)
      const newToValue = customRange ? from.clone().add(1, newViewValue) : newFromValue.clone().add(1, newViewValue)

      if (!customRange) setFrom(newFromValue)
      setTo(newToValue)
    },
    [setView, view, customRange, from, setFrom, setTo, handleGoToCurrentView, handleSwitchToYear, handleSwitchToLowerView, handleSwitchingToWeek]
  )

  const handleFromChange = useCallback(
    (_from: Moment | null) => {
      let exceedsMax
      if (!customRange) {
        exceedsMax =
          view === 'day' ? _from?.clone().startOf(view).isAfter(maxFrom?.clone().add(1, 'day')) : _from?.clone().startOf(view).isAfter(maxFrom)
        const from = exceedsMax ? maxFrom : _from?.clone().startOf(view)
        const _to = from?.clone().add(1, view)

        if (from) setFrom(from)
        if (_to) setTo(_to)
      } else {
        if (_from === null) return
        const to = getToDateValue(_from, view, true)
        setFrom(_from)
        setTo(to)
      }

      if (exceedsMax) {
        enqueueErrorStack(t('errors.exceedsMaxDate', { maxDate: maxFrom?.format(t('common.format.date')) }))
        exceedsMax = undefined
      }
    },
    [view, to, customRange, showForecast, maxFrom] //eslint-disable-line
  )

  const handleToChange = useCallback(
    (_to: Moment | null) => {
      if (!customRange) {
        const _from = _to?.clone().subtract(1, view)
        if (_from) setFrom(_from)
        if (_to) setTo(_to)
      } else {
        if (_to?.isBefore(from)) {
          setFrom(_to.clone().subtract(1, view))
        }
        if (_to) setTo(_to)
      }
    },
    [view, from, customRange, setFrom, setTo]
  )

  const handleCustomRangeChange = useCallback(
    (_customRange: boolean) => {
      if (!_customRange) {
        const _from = from.clone().startOf(view)
        const _to = getToDateValue(_from, view, false)
        setFrom(_from)
        setTo(_to)
        setCustomRange(_customRange)
      } else {
        const _from = from.clone().startOf(view)
        const _to = getToDateValue(_from.clone(), view, true)
        setFrom(_from)
        setTo(_to)
        setCustomRange(_customRange)
      }
    },
    [view, from, setFrom, setTo, setCustomRange]
  )

  const handleMinus = useCallback(() => {
    const _from = from.clone().subtract(1, view)
    const _to = _from.clone().add(1, view)
    setFrom(_from)
    setTo(_to)
  }, [view, from, setFrom, setTo])

  const handlePlus = useCallback(() => {
    const _from = from.clone().add(1, view)
    const _to = _from.clone().add(1, view)

    setFrom(_from)
    setTo(_to)
  }, [view, from, setFrom, setTo])

  const handleShowForecast = useCallback(
    (_showForecast: boolean) => {
      setIsForecastEnabled?.(_showForecast)
    },
    [setIsForecastEnabled]
  )

  return [handleViewChange, handleFromChange, handleToChange, handleCustomRangeChange, handleMinus, handlePlus, handleShowForecast]
}

export const getChartName = (customLayout: CustomLayout | undefined, t: TFunction): string | undefined => {
  return customLayout?.isInitial ? t('systemDataHandler.presets.' + customLayout.title) : customLayout?.title
}
